import React from "react";
import ReactDOM from "react-dom";
/* =========================================================
   珂玺资本 — App shell
   Logo · Nav (incl mobile/dropdown/settings) · Footer · helpers
   ========================================================= */

const { useState, useEffect, useRef, useCallback, useMemo } = React;

const D = window.SiteData;

// -----------------------------------------------------------
// 滚动锁定：弹窗打开时禁止背景页面滚动（计数式，支持多个弹窗叠加）。
// 用法：在弹窗组件里 window.useScrollLock(open)。
// -----------------------------------------------------------
let _scrollLockCount = 0;
let _scrollLockPrev = { body: "", html: "" };
function useScrollLock(active) {
  useEffect(() => {
    if (!active) return;
    if (_scrollLockCount === 0) {
      _scrollLockPrev = { body: document.body.style.overflow, html: document.documentElement.style.overflow };
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    }
    _scrollLockCount++;
    return () => {
      _scrollLockCount--;
      if (_scrollLockCount === 0) {
        document.body.style.overflow = _scrollLockPrev.body;
        document.documentElement.style.overflow = _scrollLockPrev.html;
      }
    };
  }, [active]);
}

// -----------------------------------------------------------
// 弹窗快捷键：ESC 取消、Enter 提交。
// Enter 在 textarea 内放行（正常换行）；onSubmit 省略则 Enter 不处理。
// -----------------------------------------------------------
function useModalKeys(active, { onClose, onSubmit } = {}) {
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === "Escape") { onClose && onClose(); return; }
      if (e.key === "Enter" && onSubmit) {
        const tag = e.target && e.target.tagName;
        if (tag === "TEXTAREA") return;      // textarea 内 Enter 正常换行
        e.preventDefault();
        onSubmit();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [active, onClose, onSubmit]);
}
Object.assign(window, { useScrollLock, useModalKeys });

// -----------------------------------------------------------
// Language hook
// -----------------------------------------------------------
function useLang() {
  const [lang, setLangState] = useState(() => localStorage.getItem("kexi_lang") || "cn");
  const setLang = useCallback((v) => {
    localStorage.setItem("kexi_lang", v);
    setLangState(v);
  }, []);
  return { lang, setLang };
}

// -----------------------------------------------------------
// Logo
// -----------------------------------------------------------
function Logo({ size = "sm", onClick }) {
  const fs = size === "lg" ? 64 : size === "md" ? 36 : 22;
  const en = size === "lg" ? 14 : size === "md" ? 11 : 9;
  return (
    <button className="logo" onClick={onClick} aria-label="珂玺资本" style={{
      display: "inline-flex", alignItems: "center", gap: fs * 0.55,
    }}>
      <span style={{
        width: fs * 1.5, height: fs * 1.5, flexShrink: 0,
        border: "1px solid var(--gold)", color: "var(--gold)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontFamily: "var(--f-serif-cn)", fontWeight: 500,
        fontSize: fs * 0.7, lineHeight: 1, position: "relative",
      }}>
        珂
        <i style={{ position: "absolute", top: 3, left: 3, width: 6, height: 1, background: "var(--gold)" }} />
        <i style={{ position: "absolute", top: 3, left: 3, width: 1, height: 6, background: "var(--gold)" }} />
        <i style={{ position: "absolute", bottom: 3, right: 3, width: 6, height: 1, background: "var(--gold)" }} />
        <i style={{ position: "absolute", bottom: 3, right: 3, width: 1, height: 6, background: "var(--gold)" }} />
      </span>
      <span style={{
        display: "inline-flex", flexDirection: "column", alignItems: "flex-start",
        whiteSpace: "nowrap", lineHeight: 1,
      }}>
        <span style={{
          fontFamily: "var(--f-serif-cn)", fontSize: fs, fontWeight: 500,
          color: "var(--ivory)", letterSpacing: "0.16em", paddingLeft: "0.08em",
        }}>玺 · 资本</span>
        <span className="logo-en" style={{
          marginTop: fs * 0.35, fontFamily: "var(--f-sans-en)", fontSize: en,
          letterSpacing: "0.34em", color: "var(--gold)", textTransform: "uppercase", paddingLeft: "0.08em",
        }}>KEXI CAPITAL</span>
      </span>
    </button>
  );
}

// -----------------------------------------------------------
// Settings dropdown
// -----------------------------------------------------------
function SettingsDropdown({ lang, setLang, settings, setSetting, resetSettings }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const t = (keyOrCn, en) => {
    if (typeof keyOrCn === "string" && keyOrCn.indexOf(".") > 0 && window.I18N) {
      const [ns, k] = keyOrCn.split(".", 2);
      const val = window.I18N[lang] && window.I18N[lang][ns] && window.I18N[lang][ns][k];
      if (val !== undefined) return val;
    }
    // fallback: 兼容旧 t(cn, en) 调用
    return lang === "cn" ? keyOrCn : (en !== undefined ? en : keyOrCn);
  };
  const T = window.THEMES, A = window.ACCENTS, CF = window.CN_FONTS, EF = window.EN_FONTS, FS = window.FONT_SIZES;
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const darkThemes = Object.entries(T).filter(([_, v]) => v.mood === "dark");
  const lightThemes = Object.entries(T).filter(([_, v]) => v.mood === "light");
  const fonts = lang === "cn" ? CF : EF;
  const fontKey = lang === "cn" ? "cnFont" : "enFont";

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(!open)}
        aria-label={t('common.t001')}
        title={t('common.t002')}
        style={{
          width: 36, height: 36,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: open ? "var(--gold)" : "var(--ivory-2)",
          border: `1px solid ${open ? "var(--gold)" : "transparent"}`,
          background: open ? "rgba(201,168,107,0.06)" : "transparent",
          transition: "all .25s var(--ease-out)",
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46a.5.5 0 00-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65A.488.488 0 0014 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1a.566.566 0 00-.18-.03c-.17 0-.34.09-.43.25l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98s.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46a.5.5 0 00.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.06.02.12.03.18.03.17 0 .34-.09.43-.25l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z" fill="currentColor"/>
        </svg>
      </button>

      {open && (
        <div className="settings-panel" style={{
          position: isMobile ? "fixed" : "absolute",
          top: isMobile ? "var(--nav-h)" : "calc(100% + 14px)",
          right: isMobile ? 0 : 0,
          left: isMobile ? 0 : "auto",
          width: isMobile ? "100vw" : 360,
          maxHeight: isMobile ? "calc(100vh - var(--nav-h))" : "calc(100vh - 120px)",
          overflowY: "auto",
          background: "var(--bg-1)",
          border: "1px solid var(--gold-dim)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
          zIndex: 9999,
          animation: "settingsFade .25s var(--ease-out)",
          zoom: 1 / (window.FONT_SIZES?.[settings?.fontSize]?.scale || 1),
        }}>
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 1,
            background: "linear-gradient(90deg, transparent, var(--gold) 50%, transparent)",
          }} />
          <div style={{
            padding: "18px 22px 14px",
            borderBottom: "1px solid var(--line-2)",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div>
              <div className="mono" style={{ color: "var(--gold)", fontSize: 10, letterSpacing: "0.16em" }}>· SETTINGS</div>
              <div style={{ fontFamily: "var(--f-serif-cn)", fontSize: 17, color: "var(--ivory)", marginTop: 4 }}>
                {t('common.t003')}
              </div>
            </div>
            <button onClick={() => setOpen(false)} style={{ color: "var(--mute)", fontSize: 22, padding: 4, lineHeight: 1 }}>×</button>
          </div>

          <div style={{ padding: "18px 22px 22px", display: "flex", flexDirection: "column", gap: 22 }}>
            {/* Language */}
            <SettingGroup label={t('common.t004')}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {[{ v: "cn", l: "中文 简体" }, { v: "en", l: "English" }].map(o => (
                  <button key={o.v} onClick={() => setLang(o.v)} style={{
                    padding: "10px 14px",
                    border: `1px solid ${lang === o.v ? "var(--gold)" : "var(--line)"}`,
                    background: lang === o.v ? "rgba(201,168,107,0.06)" : "transparent",
                    color: lang === o.v ? "var(--ivory)" : "var(--mute)",
                    fontSize: 13, letterSpacing: "0.04em",
                  }}>{o.l}</button>
                ))}
              </div>
            </SettingGroup>

            {/* Theme — light */}
            <SettingGroup label={t('common.t005')}>
              <ThemeSwatchRow themes={lightThemes} value={settings.theme} onChange={(v) => setSetting("theme", v)} lang={lang} />
            </SettingGroup>

            {/* Theme — dark */}
            <SettingGroup label={t('common.t006')}>
              <ThemeSwatchRow themes={darkThemes} value={settings.theme} onChange={(v) => setSetting("theme", v)} lang={lang} />
            </SettingGroup>

            {/* Accent */}
            <SettingGroup label={t('common.t007')}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                {Object.entries(A).map(([k, v]) => (
                  <button key={k} onClick={() => setSetting("accent", k)} style={{
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                    padding: "12px 4px",
                    border: `1px solid ${settings.accent === k ? "var(--gold)" : "var(--line)"}`,
                    background: settings.accent === k ? "rgba(201,168,107,0.06)" : "transparent",
                  }}>
                    <span style={{ width: 28, height: 28, background: v.swatch, borderRadius: "50%" }} />
                    <span style={{ fontSize: 11, color: settings.accent === k ? "var(--gold)" : "var(--mute)" }}>
                      {lang === "cn" ? v.cn : v.en}
                    </span>
                  </button>
                ))}
              </div>
            </SettingGroup>

            {/* Font size */}
            <SettingGroup label={t('common.t008')}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                {Object.entries(FS).map(([k, v]) => {
                  const active = settings.fontSize === k;
                  return (
                    <button key={k} onClick={() => setSetting("fontSize", k)} style={{
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                      padding: "12px 4px",
                      border: `1px solid ${active ? "var(--gold)" : "var(--line)"}`,
                      background: active ? "rgba(201,168,107,0.06)" : "transparent",
                    }}>
                      <span style={{
                        fontFamily: "var(--f-serif-cn)",
                        fontSize: 12 + (v.scale - 1) * 16,
                        color: active ? "var(--ivory)" : "var(--ivory-2)",
                        lineHeight: 1,
                      }}>文</span>
                      <span style={{ fontSize: 11, color: active ? "var(--gold)" : "var(--mute)" }}>
                        {lang === "cn" ? v.cn : v.en}
                      </span>
                    </button>
                  );
                })}
              </div>
            </SettingGroup>

            {/* Font — dynamic to lang */}
            <SettingGroup label={lang === "cn" ? "中文字体" : "Latin typeface"}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {Object.entries(fonts).map(([k, f]) => {
                  const active = settings[fontKey] === k;
                  return (
                    <button key={k} onClick={() => setSetting(fontKey, k)} style={{
                      display: "grid", gridTemplateColumns: "auto 1fr auto",
                      gap: 14, alignItems: "center",
                      padding: "12px 14px",
                      border: `1px solid ${active ? "var(--gold)" : "var(--line)"}`,
                      background: active ? "rgba(201,168,107,0.06)" : "transparent",
                      textAlign: "left",
                    }}>
                      <span style={{
                        fontFamily: f.stack,
                        fontSize: lang === "cn" ? 18 : 17,
                        color: active ? "var(--ivory)" : "var(--ivory-2)",
                        minWidth: lang === "cn" ? 80 : 110,
                      }}>{f.sample}</span>
                      <span style={{
                        fontSize: 11, color: active ? "var(--gold)" : "var(--mute-2)",
                        letterSpacing: "0.06em", textAlign: "right",
                      }}>{f.label}</span>
                      {active && <span style={{ color: "var(--gold)", fontSize: 10 }}>●</span>}
                      {!active && <span style={{ width: 10 }} />}
                    </button>
                  );
                })}
              </div>
            </SettingGroup>

            {/* Reset */}
            <button onClick={resetSettings} style={{
              padding: "10px 0", marginTop: 4,
              borderTop: "1px solid var(--line-2)",
              color: "var(--mute)", fontSize: 11,
              letterSpacing: "0.12em", textTransform: "uppercase",
              fontFamily: "var(--f-mono)",
            }}>
              {t('common.t009')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingGroup({ label, children }) {
  return (
    <div>
      <div className="mono" style={{
        color: "var(--mute-2)", fontSize: 10, letterSpacing: "0.14em",
        textTransform: "uppercase", marginBottom: 12,
      }}>{label}</div>
      {children}
    </div>
  );
}

function ThemeSwatchRow({ themes, value, onChange, lang }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${themes.length}, 1fr)`, gap: 8 }}>
      {themes.map(([k, v]) => {
        const active = value === k;
        // Mini preview: bg + accent dot + line
        return (
          <button key={k} onClick={() => onChange(k)} style={{
            padding: 6, gap: 6,
            display: "flex", flexDirection: "column", alignItems: "center",
            border: `1px solid ${active ? "var(--gold)" : "var(--line)"}`,
            background: active ? "rgba(201,168,107,0.06)" : "transparent",
          }}>
            <div style={{
              width: "100%", height: 44,
              background: v.swatch,
              border: v.mood === "light" ? "1px solid rgba(0,0,0,0.08)" : "1px solid rgba(255,255,255,0.06)",
              position: "relative",
            }}>
              {/* tiny accent + line markers for preview */}
              <span style={{
                position: "absolute", bottom: 6, left: 6,
                width: 14, height: 1, background: "var(--gold)",
              }} />
              <span style={{
                position: "absolute", top: 6, right: 6,
                width: 5, height: 5, borderRadius: "50%", background: "var(--gold)",
              }} />
            </div>
            <span style={{ fontSize: 11, color: active ? "var(--gold)" : "var(--mute)", letterSpacing: "0.04em" }}>
              {lang === "cn" ? v.cn : v.en}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------
// Mobile menu (full-screen sheet)
// -----------------------------------------------------------
function MobileMenu({ open, onClose, route, setRoute, lang, onLoginClick }) {
  const { user, logout } = window.useAuth();
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  if (!open) return null;
  const t = (keyOrCn, en) => {
    if (typeof keyOrCn === "string" && keyOrCn.indexOf(".") > 0 && window.I18N) {
      const [ns, k] = keyOrCn.split(".", 2);
      const val = window.I18N[lang] && window.I18N[lang][ns] && window.I18N[lang][ns][k];
      if (val !== undefined) return val;
    }
    // fallback: 兼容旧 t(cn, en) 调用
    return lang === "cn" ? keyOrCn : (en !== undefined ? en : keyOrCn);
  };
  return (
    <div style={{
      position: "fixed", top: "var(--nav-h)", left: 0, right: 0, bottom: 0,
      background: "var(--bg-0)", zIndex: 90,
      overflowY: "auto",
      animation: "fadein .25s var(--ease-out)",
    }}>
      <div style={{ padding: "32px 0 80px" }}>
        {D.nav.map((n) => (
          <button key={n.id}
            onClick={() => { setRoute(n.id === "invest" ? "strategy" : n.id); onClose(); }}
            style={{
              display: "block", width: "100%", textAlign: "left",
              padding: "20px 12px",
              fontFamily: "var(--f-serif-cn)", fontSize: 28, fontWeight: 500,
              color: (route === n.id || (n.id === "invest" && ["strategy","portfolio","cases"].includes(route)))
                ? "var(--gold)" : "var(--ivory)",
              borderBottom: "1px solid var(--line-2)",
            }}>{lang === "cn" ? n.cn : n.en}</button>
        ))}

        {/* 用户入口（桌面端在右上角 UserMenu，移动端被 CSS 隐藏，故在此提供） */}
        <div style={{ marginTop: 28, paddingTop: 8 }}>
          {user ? (
            <>
              <button onClick={() => { setRoute("dashboard"); onClose(); }} style={{
                display: "block", width: "100%", textAlign: "left", padding: "18px 12px",
                fontFamily: "var(--f-serif-cn)", fontSize: 20, fontWeight: 500,
                color: route === "dashboard" ? "var(--gold)" : "var(--ivory)",
                borderBottom: "1px solid var(--line-2)",
              }}>
                {lang === "cn" ? "用户中心" : "Dashboard"}
                <span className="mono" style={{ fontSize: 11, color: "var(--mute-2)", marginLeft: 12, letterSpacing: "0.06em" }}>{user.display_name}</span>
              </button>
              <button onClick={() => { onClose(); window.requestLogout(() => logout()); }} style={{
                display: "block", width: "100%", textAlign: "left", padding: "18px 12px",
                fontSize: 14, color: "var(--mute)",
              }}>{lang === "cn" ? "退出登录" : "Sign out"}</button>
            </>
          ) : (
            <button onClick={() => { onClose(); onLoginClick && onLoginClick(); }} style={{
              display: "block", width: "100%", textAlign: "left", padding: "18px 12px",
              fontFamily: "var(--f-serif-cn)", fontSize: 20, fontWeight: 500, color: "var(--gold)",
              borderBottom: "1px solid var(--line-2)",
            }}>{(window.I18N?.[lang]?.common?.t022) || (lang === "cn" ? "登录 / 注册" : "Sign in")}</button>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------
// User menu (登录后：用户名 → 用户中心 / 退出登录)
// -----------------------------------------------------------
function UserMenu({ user, lang, goTo }) {
  const isZh = lang === "cn";
  const { logout } = window.useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onEsc); };
  }, [open]);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button className="btn btn-ghost nav-cta-desktop" style={{ padding: "10px 18px", fontSize: 12 }}
        onClick={() => setOpen((v) => !v)}>
        {user.display_name}
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 10px)", right: 0, minWidth: 180,
          background: "var(--bg-1)", border: "1px solid var(--gold-dim)",
          boxShadow: "0 18px 44px rgba(0,0,0,0.35)", zIndex: 9999,
          animation: "settingsFade .2s var(--ease-out)",
        }}>
          <button onClick={() => { setOpen(false); goTo("dashboard"); }} style={{
            display: "block", width: "100%", textAlign: "left", padding: "14px 18px",
            color: "var(--ivory)", fontSize: 13, borderBottom: "1px solid var(--line-2)",
          }}>{isZh ? "用户中心" : "Dashboard"}</button>
          <button onClick={() => { setOpen(false); window.requestLogout(() => logout()); }} style={{
            display: "block", width: "100%", textAlign: "left", padding: "14px 18px",
            color: "var(--mute)", fontSize: 13,
          }}>{isZh ? "退出登录" : "Sign out"}</button>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------
// Nav
// -----------------------------------------------------------
function Nav({ route, setRoute, lang, setLang, onLoginClick, settings, setSetting, resetSettings }) {
  const { user } = window.useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [compact, setCompact] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const lastY = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 40);
      // 下滚 → 压成细条；上滚 → 恢复
      const diff = y - lastY.current;
      if (y > 120 && diff > 2) setCompact(true);
      else if (diff < -2 || y < 60) setCompact(false);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => { setRoute(id); setOpenDropdown(null); setMobileOpen(false); window.scrollTo({ top: 0 }); };

  return (
    <>
      <nav className={`nav ${scrolled ? "scrolled" : ""} ${compact && !mobileOpen ? "compact" : ""}`}>
        <div className="container-wide nav-inner">
          <div className="nav-logo"><Logo onClick={() => goTo("home")} /></div>

          {/* Desktop links */}
          <div className="nav-links">
            {D.nav.map((n) => {
              // "invest" menu is active when route is strategy/portfolio/cases too
              const investRoutes = ["strategy", "portfolio", "cases"];
              const active = n.id === "invest"
                ? (route === "invest" || investRoutes.includes(route))
                : route === n.id;
              const handleClick = () => {
                if (n.id === "invest") {
                  // If already on an invest sub-route, keep it; otherwise default to strategy
                  if (!investRoutes.includes(route)) goTo("strategy");
                  else goTo(route);
                } else {
                  goTo(n.id);
                }
              };
              return (
                <button key={n.id} className={`nav-link ${active ? "active" : ""}`} onClick={handleClick}>
                  {lang === "cn" ? n.cn : n.en}
                </button>
              );
            })}
          </div>

          {/* Right cluster */}
          <div className="nav-right">
            {user ? (
              <UserMenu user={user} lang={lang} goTo={goTo} />
            ) : (
              <button className="btn btn-ghost nav-cta-desktop" style={{ padding: "10px 18px", fontSize: 12 }}
                onClick={onLoginClick}>
                {(window.I18N?.[lang]?.common?.t022) || (lang === "cn" ? "登录 / 注册" : "Sign in")}
              </button>
            )}
            {settings && setSetting && (
              <SettingsDropdown lang={lang} setLang={setLang} settings={settings} setSetting={setSetting} resetSettings={resetSettings} />
            )}
            <button className="nav-burger" aria-label="Menu"
              onClick={() => setMobileOpen(!mobileOpen)}>
              <span style={{
                position: "absolute",
                width: 20, height: 1.5, background: "currentColor",
                transform: mobileOpen ? "rotate(45deg)" : "translateY(-6px)",
                transition: "transform .25s",
              }} />
              <span style={{
                position: "absolute",
                width: 20, height: 1.5, background: "currentColor",
                opacity: mobileOpen ? 0 : 1,
                transition: "opacity .2s",
              }} />
              <span style={{
                position: "absolute",
                width: 20, height: 1.5, background: "currentColor",
                transform: mobileOpen ? "rotate(-45deg)" : "translateY(6px)",
                transition: "transform .25s",
              }} />
            </button>
          </div>
        </div>
      </nav>
      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} route={route} setRoute={setRoute} lang={lang} onLoginClick={onLoginClick} />
    </>
  );
}

// -----------------------------------------------------------
// Back-to-top
// -----------------------------------------------------------
function BackToTop({ lang, backTo }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const fabStyle = (visible, bottom = 24) => ({
    position: "fixed",
    bottom,
    width: 48, height: 48,
    background: "var(--bg-1)",
    border: "1px solid var(--gold)",
    color: "var(--gold)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 90,
    opacity: visible ? 1 : 0,
    pointerEvents: visible ? "auto" : "none",
    transform: visible ? "translateY(0)" : "translateY(12px)",
    transition: "all .35s var(--ease-out)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
  });
  return (
    <>
      {backTo && (
        <button
          onClick={backTo}
          aria-label={lang === "cn" ? "返回上一页" : "Go back"}
          title={lang === "cn" ? "返回上一页" : "Go back"}
          className="back-prev"
          style={{
            ...fabStyle(show),
            top: "50%", bottom: "auto", right: 24,
            transform: show ? "translateY(-50%)" : "translateY(calc(-50% + 12px))",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 7h8M7 3L3 7l4 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label={lang === "cn" ? "回到顶部" : "Back to top"}
        title={lang === "cn" ? "回到顶部" : "Back to top"}
        className="back-top"
        style={{ ...fabStyle(show), right: 24 }}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M7 11V3M3 7l4-4 4 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </>
  );
}

// -----------------------------------------------------------
// Footer
// -----------------------------------------------------------
function Footer({ lang, setRoute }) {
  const t = (keyOrCn, en) => {
    if (typeof keyOrCn === "string" && keyOrCn.indexOf(".") > 0 && window.I18N) {
      const [ns, k] = keyOrCn.split(".", 2);
      const val = window.I18N[lang] && window.I18N[lang][ns] && window.I18N[lang][ns][k];
      if (val !== undefined) return val;
    }
    // fallback: 兼容旧 t(cn, en) 调用
    return lang === "cn" ? keyOrCn : (en !== undefined ? en : keyOrCn);
  };
  // "投资"父项替换为三个子页面，不显示父项本身
  const flatNav = D.nav.flatMap(n => {
    if (n.id === "invest") return D.investTabs;
    return [n];
  });
  return (
    <footer className="footer">
      <div className="container-wide">
        <div className="footer-grid">
          <div>
            <Logo size="md" />
            <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 28, maxWidth: 360, lineHeight: 1.7 }}>
              {t('common.t023')}
            </p>
            <div style={{ marginTop: 28, display: "flex", gap: 12, flexWrap: "wrap" }}>
              {[
                { label: "WeChat", abbr: t('common.t025') },
                { label: "Weibo", abbr: t('common.t026') },
                { label: "LinkedIn", abbr: t('common.t027') },
              ].map(s => (
                <div key={s.label} className="social-btn" title={s.label}>{s.abbr}</div>
              ))}
            </div>
          </div>
          <div className="footer-col">
            <h4>{t('common.t010')}</h4>
            {flatNav.slice(0, Math.ceil(flatNav.length / 2)).map(n => (
              <a key={n.id} onClick={() => { setRoute(n.id); window.scrollTo({ top: 0 }); }} style={{ cursor: "pointer" }}>
                {lang === "cn" ? n.cn : n.en}
              </a>
            ))}
          </div>
          <div className="footer-col">
            <h4>&nbsp;</h4>
            {flatNav.slice(Math.ceil(flatNav.length / 2)).map(n => (
              <a key={n.id} onClick={() => { setRoute(n.id); window.scrollTo({ top: 0 }); }} style={{ cursor: "pointer" }}>
                {lang === "cn" ? n.cn : n.en}
              </a>
            ))}
          </div>
          <div className="footer-col">
            <h4>{t('common.t011')}</h4>
            <a>{D.contact.phone}</a>
            <a>{D.contact.email}</a>
            <a style={{ marginTop: 12, color: "var(--mute-2)", fontSize: 11 }}>
              {lang === "cn" ? D.contact.addr.cn : D.contact.addr.en}
            </a>
          </div>
        </div>

        <div style={{ padding: "32px 0", borderTop: "1px solid var(--line-2)", maxWidth: 900 }}>
          <p style={{ color: "var(--mute-2)", fontSize: 11, lineHeight: 1.7, margin: 0, letterSpacing: "0.02em" }}>
            <span style={{ color: "var(--gold)", fontFamily: "var(--f-mono)", marginRight: 12, fontSize: 10, letterSpacing: "0.12em" }}>DISCLAIMER</span>
            {lang === "cn" ? D.footer.discl.cn : D.footer.discl.en}
          </p>
        </div>

        <div className="footer-bottom">
          <div>{t('common.t024')}</div>
          <div>{t('common.t012')}</div>
        </div>
      </div>
    </footer>
  );
}

// -----------------------------------------------------------
// Counter — animated number
// -----------------------------------------------------------
function Counter({ to, duration = 1800, suffix = "", className = "", delay = 200, numFixed = null, suffixFixed = null, bilingual = false }) {
  const target = parseFloat(to);
  const isNum = !isNaN(target);
  const [n, setN] = useState(isNum ? 0 : to);
  useEffect(() => {
    if (!isNum) { setN(to); return; }
    const start = performance.now() + delay;
    let raf;
    const tick = (now) => {
      if (now < start) { raf = requestAnimationFrame(tick); return; }
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(target * eased * 10) / 10);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setN(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, duration, delay]);
  const display = isNum ? (Number.isInteger(target) ? Math.round(n) : n) : n;
  // numFixed / suffixFixed：把数字 / 后缀绑定到指定字段。
  // bilingual=false（默认）→ 语言中性单框；bilingual=true → 中英双框（后续可改文字）。
  const nAttr = numFixed ? { "data-cpath-fixed": numFixed, ...(bilingual ? {} : { "data-cms-neutral": true }) } : {};
  const sAttr = suffixFixed ? { "data-cpath-fixed": suffixFixed, ...(bilingual ? {} : { "data-cms-neutral": true }) } : {};
  return (
    <span className={className}>
      {numFixed ? <span {...nAttr}>{display}</span> : display}
      {suffixFixed
        ? <span className="unit" {...sAttr}>{suffix}</span>
        : <span className="unit">{suffix}</span>}
    </span>
  );
}

// -----------------------------------------------------------
// Section head + shared bits
// -----------------------------------------------------------
function SectionHead({ num, kicker, title, titleEn, lead, lang, action, leadWide }) {
  return (
    <div className={`section-head${leadWide ? " lead-wide" : ""}`} style={{ display: "block" }}>
      {/* 序号行：左侧序号+kicker，右侧 action 按钮（num/kicker 皆空则不渲染） */}
      {(num || kicker || action) && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 24 }}>
          <div>
            {num && <div className="num">{num}</div>}
            {kicker && <div className="kicker" style={{ marginTop: 8 }}>{kicker}</div>}
          </div>
          {action && <div>{action}</div>}
        </div>
      )}
      {/* 标题+lead */}
      <h2 className="h-1">{title}</h2>
      {titleEn && lang === "cn" && <div className="serif-en" style={{ color: "var(--gold)", marginTop: 8, fontSize: 22 }}>{titleEn}</div>}
      {lead && <p className="lead" style={{ maxWidth: leadWide ? 820 : 560 }}>{lead}</p>}
    </div>
  );
}

function StatGrid({ lang }) {
  const t = (k) => { const [ns, key] = k.split("."); return (window.I18N?.[lang]?.[ns]?.[key]) ?? k; };
  return (
    <div className="stat-grid">
      {D.stats.map((s, i) => (
        <div className="stat-cell" key={i}>
          <div className="index" data-cpath-fixed={`site_data.stats.${i}.index`} data-cms-neutral>{s.index}</div>
          <div className="stat-num">
            <Counter to={t('main.statN' + (i + 1))} suffix={t('main.statU' + (i + 1))}
              numFixed={`main.statN${i + 1}`} suffixFixed={`main.statU${i + 1}`} bilingual />
          </div>
          <div className="stat-label">{lang === "cn" ? s.cn : s.en}</div>
        </div>
      ))}
    </div>
  );
}

function ListedMarquee({ lang }) {
  const items = D.listed.concat(D.listed);
  // 上市公司名 + 股票代码来自「投资项目」菜单的成员，预览里右键给跳转提示
  return (
    <div className="marquee" data-cms-redirect="projects">
      <div className="marquee-track">
        {items.map((c, i) => (
          <div className="marquee-item" key={i}>
            <span>{lang === "cn" ? c.cn : c.en}</span>
            <span style={{ fontFamily: "var(--f-mono)", fontSize: 11, color: "var(--gold-soft)", letterSpacing: "0.08em" }}>
              {c.ticker}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PhImg({ kind = "skyline", label, style }) {
  return (
    <div className={`ph-img ${kind === "skyline" ? "skyline" : ""}`} style={style}>
      <span className="ph-label">{label}</span>
    </div>
  );
}

// 可编辑图片：有 url 显示真实图（按 fit 适配），否则显示占位。
// data-cms-image 让编辑模式右键定位到该图字段（cpath 指向 {url,fit} 对象）。
function fileUrlS(key) {
  if (!key) return "";
  if (/^https?:\/\//.test(key) || key.startsWith("/")) return key;
  return `/api/files/${key}`;
}

// 全站图片点击放大：任意图片 onClick 调 zoomImage(url) 即可弹出全屏查看。
// 编辑模式（?edit=1，body.cms-edit）下不放大，避免与内联编辑冲突。
function zoomImage(url) {
  if (!url) return;
  if (document.body.classList.contains("cms-edit")) return;
  window.dispatchEvent(new CustomEvent("kexi-zoom", { detail: url }));
}
// 挂在 App 根的全局放大查看层（与动态文章 / 公司详情同款：适配窗口↔原图、关闭、ESC、锁滚动）
function GlobalLightbox() {
  const [url, setUrl] = useState(null);
  const [actual, setActual] = useState(false);
  const close = () => { setUrl(null); setActual(false); };
  useEffect(() => {
    const onZoom = (e) => { setUrl(e.detail); setActual(false); };
    window.addEventListener("kexi-zoom", onZoom);
    return () => window.removeEventListener("kexi-zoom", onZoom);
  }, []);
  useEffect(() => {
    if (!url) return;
    const onEsc = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onEsc);
    const pb = document.body.style.overflow, ph = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = pb;
      document.documentElement.style.overflow = ph;
    };
  }, [url]);
  if (!url) return null;
  return (
    <div className={`img-lightbox ${actual ? "is-actual" : "is-fit"}`} onClick={close}>
      <button className="img-lightbox-close" aria-label="关闭" onClick={(e) => { e.stopPropagation(); close(); }}>×</button>
      <img src={url} alt="" onClick={(e) => { e.stopPropagation(); setActual((v) => !v); }} />
    </div>
  );
}

function EditableImage({ url, fit = "width", cpath, label, kind = "skyline", style }) {
  const imgStyle = fit === "height"
    ? { width: "100%", height: "100%", objectFit: "cover", display: "block" }
    : { width: "100%", height: "auto", display: "block" };
  // 有图时用 cms-haspic（无占位背景/伪元素）；无图时复用 ph-img 占位样式
  return url ? (
    <div className="cms-haspic" style={{ overflow: "hidden", ...style }}
      data-cms-image={cpath}>
      <img src={fileUrlS(url)} alt={label || ""} style={{ ...imgStyle, cursor: "zoom-in" }}
        onClick={() => zoomImage(fileUrlS(url))} />
    </div>
  ) : (
    <div className={`ph-img ${kind === "skyline" ? "skyline" : ""}`} style={style}
      data-cms-image={cpath}>
      <span className="ph-label">{label}</span>
    </div>
  );
}

// 退出登录确认：任意位置调 requestLogout(cb) 弹确认框，确认后执行 cb（实际 logout）。
// 用事件桥 + createPortal 渲染到 body，脱离 .page 的 transform 上下文（否则 fixed 失效）。
function requestLogout(onConfirm) {
  window.dispatchEvent(new CustomEvent("kexi-logout-confirm", { detail: onConfirm }));
}
function ConfirmLogout({ lang }) {
  const isZh = lang === "cn";
  const [cb, setCb] = useState(null);
  useScrollLock(!!cb);
  useEffect(() => {
    const on = (e) => setCb(() => e.detail);
    window.addEventListener("kexi-logout-confirm", on);
    return () => window.removeEventListener("kexi-logout-confirm", on);
  }, []);
  useModalKeys(!!cb, { onClose: () => setCb(null), onSubmit: () => { const fn = cb; setCb(null); fn && fn(); } });
  if (!cb) return null;
  const close = () => setCb(null);
  const confirm = () => { close(); cb && cb(); };
  return ReactDOM.createPortal(
    <div className="modal-backdrop" onClick={close} style={{ zIndex: 10000 }}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <button className="close" onClick={close}>×</button>
        <div className="eyebrow">{isZh ? "退出登录" : "Sign out"}</div>
        <h2 className="h-2" style={{ marginTop: 16, marginBottom: 8 }}>{isZh ? "确认退出登录？" : "Sign out?"}</h2>
        <p className="body-mute" style={{ fontSize: 13, marginBottom: 28 }}>
          {isZh ? "退出后需重新登录才能查看您的资料与报告。" : "You'll need to sign in again to access your materials and reports."}
        </p>
        <div style={{ display: "flex", gap: 12 }}>
          <button className="btn btn-ghost" style={{ flex: 1, justifyContent: "center" }} onClick={close}>
            {isZh ? "取消" : "Cancel"}
          </button>
          <button className="btn" style={{ flex: 1, justifyContent: "center" }} onClick={confirm}>
            {isZh ? "退出登录" : "Sign out"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// 定向报告换码进度：任意位置调 openDirectedReport(reportId) 弹进度窗，
// 服务端向智库申请一次性授权码期间显示进度，成功自动关窗并跳转，失败窗内显示错误。
// 同 ConfirmLogout 的事件桥 + createPortal 模式。
function openDirectedReport(reportId) {
  window.dispatchEvent(new CustomEvent("kexi-report-open", { detail: reportId }));
}
function ReportOpenModal({ lang }) {
  const isZh = lang === "cn";
  const [st, setSt] = useState(null); // null | "busy" | "error"
  useScrollLock(!!st);
  useEffect(() => {
    let alive = true;
    const on = async (e) => {
      setSt("busy");
      try {
        const res = await window.authFetch(`/api/studio/report-link/${e.detail}`);
        const data = res.ok ? await res.json().catch(() => null) : null;
        if (data && data.url) {
          if (alive) setSt(null);
          // 一次性链接即用即弃：直接开新标签，被拦则当前页跳转
          if (!window.open(data.url, "_blank")) window.location.assign(data.url);
        } else if (alive) setSt("error");
      } catch { if (alive) setSt("error"); }
    };
    window.addEventListener("kexi-report-open", on);
    return () => { alive = false; window.removeEventListener("kexi-report-open", on); };
  }, []);
  const close = () => setSt(null);
  useModalKeys(st === "error", { onClose: close, onSubmit: close });
  if (!st) return null;
  return ReactDOM.createPortal(
    <div className="modal-backdrop" onClick={st === "error" ? close : undefined} style={{ zIndex: 10000 }}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
        {st === "error" && <button className="close" onClick={close}>×</button>}
        <div className="eyebrow">{isZh ? "查看报告" : "Open report"}</div>
        {st === "busy" ? (
          <>
            <div style={{ fontFamily: "var(--f-serif-cn)", fontWeight: 500, fontSize: 24, lineHeight: 1.3, marginTop: 16 }}>
              {isZh ? "正在申请授权…" : "Requesting access…"}
            </div>
            <p className="body-mute" style={{ fontSize: 13, lineHeight: 1.8, margin: "12px 0 0" }}>
              {isZh ? "正在向珂玺智库申请一次性访问授权，即将自动打开报告。" : "Fetching a one-time access link — the report will open shortly."}
            </p>
          </>
        ) : (
          <>
            <div style={{ fontFamily: "var(--f-serif-cn)", fontWeight: 500, fontSize: 24, lineHeight: 1.3, marginTop: 16 }}>
              {isZh ? "暂时无法打开" : "Unable to open"}
            </div>
            <p className="body-mute" style={{ fontSize: 13, lineHeight: 1.8, margin: "12px 0 28px" }}>
              {isZh ? "访问授权申请失败，请稍后重试；若多次失败请联系我们。" : "Access request failed — please retry later, or contact us if it persists."}
            </p>
            <div style={{ display: "flex" }}>
              <button className="btn" style={{ flex: 1, justifyContent: "center" }} onClick={close}>
                {isZh ? "关闭" : "Close"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

// 投资模块共享子菜单（投资策略 / 投资组合 / 上市案例）
function InvestTabs({ route, setRoute, lang }) {
  return (
    <section style={{ paddingTop: "calc(var(--nav-h) + 24px)", paddingBottom: 0 }}>
      <div className="container-wide">
        <div className="tabs" style={{ marginBottom: 0 }}>
          {D.investTabs.map(tb => (
            <button key={tb.id}
              className={`tab ${route === tb.id ? "active" : ""}`}
              onClick={() => setRoute(tb.id)}>
              {lang === "cn" ? tb.cn : tb.en}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------
Object.assign(window, {
  useLang, Logo, Nav, SettingsDropdown, MobileMenu, BackToTop,
  Footer, Counter, SectionHead, StatGrid, ListedMarquee, PhImg, EditableImage, InvestTabs,
  fileUrlS, zoomImage, GlobalLightbox, requestLogout, ConfirmLogout,
  openDirectedReport, ReportOpenModal,
});
